/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
*/
import { Params } from '@angular/router';
import { TenantService } from '@src/app/shared/tenant.service';
import { DashboardChart } from '@src/app/shared/dashboard/dashboard-widget/dashboard-chart';

import * as Highcharts from 'highcharts';
import { HttpParams } from '@angular/common/http';
import { uuid } from '@src/app/model/abstract-object';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';

class RbuData {

    startDate: string;
    startTime: number;

    endDate: string;
    endTime: number;

    rows: { srStatus: string; subStatus: string, userId: uuid, userName: string, actions: number }[];
}

class  ChartPoint { 
    y = 0;
    constructor(
        public name: string,
        public srStatus: string,
        public routeParams: Params,
    ) {}
}
class SeriesItem {
    id: uuid;
    title!: string;
    data: ChartPoint[] = [];
    setDataItem(subStatus: string, y: number) {
        if (subStatus === ServiceRequest.srAccepted.id) {
            this.data[0].y = y;
        } else if (subStatus === ServiceRequest.srResolved.id) {
            this.data[1].y = y;
        } else if (subStatus === ServiceRequest.srPlanned.id) {
            this.data[2].y = y;
        } else if (subStatus === ServiceRequest.srUnplanned.id) {
            this.data[3].y = y;
        } else if (subStatus === ServiceRequest.srOverdue.id) {
            this.data[4].y = y;
        } else {
            console.error('Unknown status ', {subStatus, y});
        }
    }  
}

export class RbuChart extends DashboardChart {
    dataUrl = TenantService.ajaxPath + 'agent/dashboard/rbu';
    dataParams = new HttpParams().set('week', 0);
    title = "Request Distribution";

    series: SeriesItem[] =[]
    // Reverse order to the order we create data points
    categories = [ServiceRequest.srOverdue.name, ServiceRequest.srUnplanned.name, ServiceRequest.srPlanned.name,
        ServiceRequest.srResolved.name, ServiceRequest.srAccepted.name];

    startDate = '';
    endDate = '';
    options = [
        { name: 'Previous Week', action: this.previousWeek.bind(this) },
        { name: 'Following Week', action: this.nextWeek.bind(this) }
    ];

    previousWeek() {
        const currWeek = this.dataParams.get('week');
        this.dataParams = this.dataParams.set('week', Number(currWeek) - 1);
        this.getData();
    }

    resetChart() {
        this.series.length = 0;
    }

    nextWeek() {
        const currWeek = this.dataParams.get('week');
        this.dataParams = this.dataParams.set('week', Number(currWeek) + 1);
        this.getData();
    }

    getSubTitle() {
        return super.getSubTitle(this.startDate + ' to ' + this.endDate)
    }

    private getSeriesItem(userId: uuid, userName: string) {
        let s = this.series.find (s => s.id === userId);
        if (!s) {
            s = new SeriesItem();
            s.id = userId;
            s.title = userName;
            s.data.push(new ChartPoint( userName, ServiceRequest.srAccepted.name,
                {resolvedById: s.id, srStatus: ServiceRequest.srAccepted.id, completedAt: `><${this.startDate}:${this.endDate}`}
            ));
            s.data.push(new ChartPoint( userName, ServiceRequest.srResolved.name,
                {resolvedById: s.id, srStatus: ServiceRequest.srResolved.id, completedAt: `><${this.startDate}:${this.endDate}`}
            ));
            s.data.push(new ChartPoint( userName, ServiceRequest.srPlanned.name,
                {ownedById: s.id, srStatus: ServiceRequest.srOpen.id, subStatus: ServiceRequest.srPlanned.id}
            ));
            s.data.push(new ChartPoint( userName, ServiceRequest.srUnplanned.name,
                {ownedById: s.id, srStatus: ServiceRequest.srOpen.id, subStatus: ServiceRequest.srUnplanned.id}
            ));
            s.data.push(new ChartPoint( userName, ServiceRequest.srOverdue.name,
                {ownedById: s.id, srStatus: ServiceRequest.srOpen.id, subStatus: ServiceRequest.srOverdue.id}
            ));
            this.series.push(s);
        }
        return s;
    }
    protected setupData(data: RbuData) {
        this.startDate = data.startDate;
        this.endDate = data.endDate;
        data.rows.forEach( x => {
            const s = this.getSeriesItem(x.userId, x.userName);
            s.setDataItem(x.subStatus, x.actions);
        })
    }

    clickEvent($event) {
        const pointParams: Params = { ...$event.point.routeParams };
        const responsibleUserField = 'resolvedById' in pointParams ? 'resolvedById' : 'ownedById';
        const routeInfo = ServiceRequestPageNavRoute.getListRouterLink(
            this.getShowParms(pointParams, responsibleUserField) as Params);
        this.router.navigate(routeInfo);
    }

    protected createChartOptions(): Highcharts.Options {

        const series: Highcharts.SeriesOptionsType[] = this.categories.map(cat => ({
            type: 'column',
            name: cat,
            data: this.series.map(user => {
                const point = user.data.find(d => d.srStatus === cat);
                return {...point, userTitle: cat}
            })
        }));

        const chartOptions: Highcharts.Options = {
            colors: ['darkred', 'red', 'orange', 'palegreen', 'green', 'darkgreen', ],
            title: this.getChartTitle(),
            subtitle: this.getSubTitle(),
            chart: {
                backgroundColor: this.chartBackgroundColor,
                borderRadius: this.chartBorderRadius,
                plotBorderWidth: null,
                type: 'column',
                height: this.height,
                width: this.width
            },
            tooltip: {
                pointFormatter: function () {
                    return `<b>${this.series.name}<b>: ${this.y}`;
                }
            },
            credits: { enabled: false },        
            xAxis: { categories: this.series.map(s => s.title) },
            yAxis: { min: 0, title: { text: 'Count' } },
            plotOptions: {
                series: {
                    stacking: 'normal',
                    point: {
                        events: {
                            click: this.clickEvent.bind(this)
                        }
                    }
                }
            },
            series
        };
        return chartOptions;
    }
}
